import React, { useState, useEffect } from 'react';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, FileText, Star, ShoppingCart, Loader2 } from 'lucide-react';
import { productsAPI, paymentsAPI } from '../services/api';

export const BundleUltimate = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Custom features for this landing page
  const customFeatures = [
    '245 Professional HR Templates',
    'All 8 HR Categories Included',
    'Hiring & Onboarding Documents (35)',
    'HR Policies Library (43)',
    'Performance & Development Tools (28)',
    'Benefits & Compensation Templates (27)',
    'Termination & Offboarding Kit (41)',
    'Compliance & Documentation Tools (25)',
    'Culture, DEI & Engagement Templates (24)',
    'HR Operations Documents (22)',
    'General Labor Law & Compliance Overview Guides'
  ];

  const categoryPreviews = [
    { title: 'Hiring & Onboarding', desc: 'Offers, job descriptions, new hire checklists, onboarding workflows' },
    { title: 'HR Policies Library', desc: 'Workplace guidelines, conduct policies, leave policies, IT use, and more' },
    { title: 'Performance & Development', desc: 'PIPs, reviews, coaching tools, goal-setting forms' },
    { title: 'Benefits & Compensation', desc: 'Leave forms, enrollment tools, bonus agreements, tracking sheets' },
    { title: 'Termination & Offboarding', desc: 'Termination letters, checklists, exit interviews, final pay tools' },
    { title: 'Compliance & Documentation', desc: 'Audit forms, poster logs, I-9 checklists, general labor law summaries' },
    { title: 'Culture, DEI & Engagement', desc: 'Surveys, recognition programs, ERG guides, wellness templates' },
    { title: 'HR Operations', desc: 'SOPs, org charts, service request forms, incident reports, payroll change forms' }
  ];

  const benefits = [
    { title: 'The Complete HR Toolkit in One Place', desc: 'Get every template you need to build or streamline your HR function.' },
    { title: 'Saves 50+ Hours of Work', desc: 'Ready-to-use documents eliminate the time spent creating HR forms from scratch.' },
    { title: 'Perfect for Small Businesses, Startups & HR Teams', desc: 'Ideal for businesses without a full-time HR department or those needing better structure.' },
    { title: 'Instant Access', desc: 'All templates available immediately in Word and Google Docs formats.' },
    { title: 'Customizable to Your Business', desc: 'Easily edit and adapt templates to match your company\'s policies and workflows.' },
    { title: 'Not Legal Advice', desc: 'These templates provide general HR structure and are not legal advice. Please review with legal or compliance resources as needed.' }
  ];

  const faqs = [
    {
      question: 'How many templates are included?',
      answer: '245 professional HR templates across eight major HR categories.'
    },
    {
      question: 'What format are the templates in?',
      answer: 'Microsoft Word (.docx) and Google Docs compatible files, plus Microsoft Excel workbooks where applicable.'
    },
    {
      question: 'Are these templates state-specific?',
      answer: 'No. Templates follow general HR best practices and may require state-by-state adjustments.'
    },
    {
      question: 'Can consultants use this for multiple clients?',
      answer: 'These templates are licensed for use within your own organization. If you are a consultant or need to use them across multiple client businesses, contact us at hello@drive-hr.com for extended licensing options.'
    },
    {
      question: 'Can these be used for remote employees?',
      answer: 'Yes. All templates are easily adaptable for in-office, hybrid, and remote teams.'
    },
    {
      question: 'Do I receive updates?',
      answer: 'You will receive any updates to the templates included in this bundle.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'All sales are final. Because our products are delivered digitally and downloaded immediately, DriveHR does not offer refunds. Please review the product details carefully before purchasing.'
    }
  ];

  useEffect(() => {
    loadProduct();
  }, []);

  const loadProduct = async () => {
    try {
      const products = await productsAPI.getAll();
      const foundProduct = products.find(p => p.id === 'ultimate-hr-bundle');
      setProduct(foundProduct);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!product) return;
    
    try {
      setProcessingPayment(true);
      
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/payment-canceled`;
      
      const checkoutData = await paymentsAPI.createProductCheckout(
        product.id,
        successUrl,
        cancelUrl,
        { product_name: product.name, source: 'bundle_detail_page' }
      );
      
      window.location.href = checkoutData.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Failed to create checkout session. Please try again.');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <StaticPageHeader />
        <div className="pt-32 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-white">
      <StaticPageHeader />
      
      <div className="pt-16">
        {/* Hero Section */}
        <div
          className="text-white py-16"
          style={{ backgroundImage: 'linear-gradient(135deg, #1A56E8 0%, #0E2A6E 100%)' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-block bg-yellow-500 text-gray-900 font-semibold px-4 py-2 rounded-full mb-4">
                <Star className="w-4 h-4 inline mr-1" />
                BEST VALUE - Save $423!
              </div>
              <h1 className="text-5xl font-bold mb-4">{product.name}</h1>
              <p className="text-xl text-blue-100 mb-8">Complete HR document library with 245 editable templates covering key HR functions from hiring to compliance to offboarding.</p>
              <div className="flex items-baseline space-x-4 mb-2">
                <span className="text-3xl text-blue-300 line-through" data-testid="ultimate-hero-regular-price">$599</span>
                <span className="text-6xl font-bold" data-testid="ultimate-hero-launch-price">${product.price}</span>
                <span className="text-xl text-blue-200">launch price · one-time purchase</span>
              </div>
              <p className="text-lg text-yellow-300 mb-6">Individual bundles would cost $922. Save $423!</p>
              <Button
                size="lg"
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold text-base px-6 min-h-[48px] max-w-full"
                onClick={handlePurchase}
                disabled={processingPayment}
                data-testid="ultimate-hero-cta"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2 flex-shrink-0" />
                    Purchase Now • $499
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* What's Included Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {customFeatures.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700 text-lg">{feature}</span>
              </div>
            ))}
          </div>

          {/* Category Preview Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Template Category Previews</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categoryPreviews.map((category, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{category.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{category.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-blue-50 rounded-2xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why This Bundle?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Star className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Upgrade Your Entire HR Function?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Get instant access to 245 HR templates and build a strong, organized HR foundation today.
            </p>
            <Button
              size="lg"
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold text-base px-6 min-h-[48px] max-w-full"
              onClick={handlePurchase}
              disabled={processingPayment}
              data-testid="ultimate-final-cta"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                  Redirecting...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2 flex-shrink-0" />
                  Purchase Now • $499
                </>
              )}
            </Button>
            <p className="text-sm text-blue-100 mt-4 max-w-2xl mx-auto">
              All sales are final. Digital products are non-refundable.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
